
import React, { useState, useEffect } from 'react';

const AnimatedNumber = ({ value, duration = 1000 }) => {
    const [count, setCount] = useState(0);

    useEffect(() => {
        let startTime;
        let animationFrame;
        const targetValue = Math.round(Number(value) || 0);

        const animate = (currentTime) => {
            if (!startTime) startTime = currentTime;
            const progress = Math.min((currentTime - startTime) / duration, 1);

            // Ease out cubic function for smooth deceleration
            const ease = 1 - Math.pow(1 - progress, 3);

            setCount(Math.floor(ease * targetValue));

            if (progress < 1) {
                animationFrame = requestAnimationFrame(animate);
            }
        };

        animationFrame = requestAnimationFrame(animate);

        return () => cancelAnimationFrame(animationFrame);
    }, [value, duration]);

    return <>{count}</>;
};

const StageSnapshot = ({ stages }) => {
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsLoaded(true);
        }, 100);
        return () => clearTimeout(timer);
    }, []);

    return (
        <div className="bg-white rounded-2xl p-6 shadow-md h-full">
            <h3 className="text-lg font-bold text-gray-900 mb-6">Stage Process (Week)</h3>
            <div className="flex flex-col gap-6 overflow-y-auto pr-2 min-h-[100px] max-h-[300px]">
                {stages?.length > 0 ? (
                    stages.map((stage, index) => (
                        <div key={index} className="flex flex-col gap-2">
                            <div className="flex justify-between items-end text-sm text-gray-700">
                                <span className="font-medium text-sm text-gray-900">{stage.name}</span>
                                <div className="flex gap-4 text-[10px] font-bold">
                                    <span className="text-gray-500">
                                        Plan: <AnimatedNumber value={stage.plan} />%
                                    </span>
                                    <span className="text-gray-900">
                                        Actual: <AnimatedNumber value={stage.actual} />%
                                    </span>
                                </div>
                            </div>
                            <div className="w-full bg-slate-200 rounded-full h-2 relative">
                                <div
                                    className="h-2 rounded-full relative z-10 transition-all duration-1000 ease-out"
                                    style={{
                                        width: isLoaded ? `${Math.round(Number(stage.actual) || 0)}%` : '0%',
                                        backgroundColor: stage.color,
                                    }}
                                ></div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="flex flex-col items-center justify-center py-10 px-4">
                        {/* Icon */}
                        <div className="w-16 h-16 mb-4 rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
                            <svg
                                className="w-8 h-8 text-blue-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                                />
                            </svg>
                        </div>

                        {/* Main Message */}
                        <h4 className="text-gray-900 font-semibold text-base mb-2">
                            No Stage Data
                        </h4>

                        {/* Descriptive Text */}
                        <p className="text-gray-500 text-sm text-center max-w-xs">
                            Stage progress will be displayed here once activities are tracked for the selected week.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default StageSnapshot;
